import styled from '@emotion/styled';
import { Trans } from 'react-i18next';

import { tokens, Button, Link, Text } from '@hotjar/hotsauce';
import { useCurrentSite } from '@hotjar/site';
import { upsell as openUpsellModal } from '@hotjar/webapp/domains/revenue/upsell';
import { noop } from '@hotjar/webapp/lib/function';
import { SCALE } from '@hotjar/webapp/domains/revenue/plan/feature-group';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { getSalesLink } from '@hotjar/upsell';

const Container = styled.div`
  display: flex;
  align-items: center;
  padding: 0 ${tokens.spacer.x3} ${tokens.spacer.x3} ${tokens.spacer.x3};
`;

const StyledSalesText = styled(Text)`
  margin-left: ${tokens.spacer.x1};
`;

interface Props {
  isCoverageMeasureComplete: boolean;
  coveragePercentage: number;
  userCanContactSales: boolean;
  trackClick: (elementName: string, shownTrafficCoverage?: number | null) => void;
}

export const TrafficCoverageUpgrade = ({
  coveragePercentage,
  isCoverageMeasureComplete,
  userCanContactSales,
  trackClick,
}: Props): JSX.Element => {
  const { t } = useTranslationWithPrefix('common:sampling-widget.tracking-coverage-message.sumup');
  const currentSite = useCurrentSite().data;

  const isAtMaximumCoverage = coveragePercentage === 100;
  const isCurrentSiteOnScale = currentSite?.effectivePlan === SCALE;

  return (
    <div>
      {!isAtMaximumCoverage && (
        <>
          {userCanContactSales && isCurrentSiteOnScale && (
            <Container>
              <Button
                kind="primary"
                as="a"
                href={getSalesLink('SamplingModal')}
                isExternal
                data-testid="traffic-coverage-widget-sales-link"
              >
                {t('speak-with-sales-text')}
              </Button>
            </Container>
          )}

          {!isCurrentSiteOnScale && (
            <Container>
              <Button
                kind={
                  !isCoverageMeasureComplete || coveragePercentage >= 75 ? 'secondary' : 'primary'
                }
                onClick={() => {
                  if (!currentSite) {
                    return;
                  }

                  const referrer = isCoverageMeasureComplete
                    ? 'sampling.widget_active-traffic-coverage-rate_upgrade-to-increase-limit'
                    : 'sampling-widget.tracking-coverage-message.sumup.available-plans';

                  openUpsellModal({
                    site: currentSite,
                    referrer,
                  }).then(noop, noop);
                }}
                icon={isCoverageMeasureComplete ? 'arrow_up' : undefined}
                data-testid="traffic-coverage-widget-upsell-button"
              >
                {isCoverageMeasureComplete ? t('upgrade-plan') : t('available-plans')}
              </Button>
            </Container>
          )}

          {userCanContactSales &&
            isCoverageMeasureComplete &&
            coveragePercentage <= 20 &&
            !isCurrentSiteOnScale && (
              <Container>
                <StyledSalesText textColor="text_light" textType="body2">
                  <Trans i18nKey="common:sampling-widget.tracking-coverage-message.sumup.speak-with-sales">
                    or
                    <Link
                      href={getSalesLink('SamplingModal')}
                      kind="inline"
                      isExternal
                      as="a"
                      onClick={() => {
                        const elementName = 'active-traffic-coverage-speak-with-sales-link';
                        trackClick(elementName, coveragePercentage);
                      }}
                    >
                      speak with sales
                    </Link>
                  </Trans>
                </StyledSalesText>
              </Container>
            )}
        </>
      )}
    </div>
  );
};
